import {
  CalloutTagStyle,
  CalloutTerminatorStyle,
  type CalloutTag,
  type CalloutTerminator,
} from "sketch/shapes/callout";
import {
  DimRefEndStyle,
  DimRefStartStyle,
  type DimRef,
} from "sketch/shapes/dim";
import {
  DefaultStylePanel,
  DefaultStylePanelContent,
  StylePanelDoubleDropdownPicker,
  StylePanelDropdownPicker,
  useEditor,
  useValue,
  type StyleValuesForUi,
} from "tldraw";

/**
 * The default style panel plus the drafting pickers. Each shows exactly when
 * its shape's styles are in the shared-styles map: the tool is active, or
 * one of its shapes is selected.
 *
 * - Dim reference: what each end measures to, in the same two-sided control
 *   the arrow shape uses for its arrowheads. Tick = face of stud (default),
 *   arrow = face of finish, dot = a construction line the drafter draws and
 *   describes (typically a centerline).
 * - Callout head and leader: lettering or a keynote tag, and what the leader
 *   lands on (an edge, a surface, or nothing).
 */
const REF_ITEMS: StyleValuesForUi<DimRef> = [
  {
    value: "stud",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <line
          x1="4"
          y1="12"
          x2="12"
          y2="4"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
  {
    value: "finish",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <path d="M 14 8 L 5 4.5 L 5 11.5 Z" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "construction",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="3" fill="currentColor" />
      </svg>
    ),
  },
];

/** The callout's head: plain lettering, or a keynote tag holding the key. */
const TAG_ITEMS: StyleValuesForUi<CalloutTag> = [
  {
    value: "none",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <path
          d="M 2 11 L 6 7 L 14 7"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
        />
        <line
          x1="6"
          y1="4"
          x2="14"
          y2="4"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
  {
    value: "circle",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <circle
          cx="8"
          cy="8"
          r="5"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
    ),
  },
  {
    value: "hex",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <polygon
          points="13,8 10.5,12.3 5.5,12.3 3,8 5.5,3.7 10.5,3.7"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
    ),
  },
];

/** What the leader lands on. */
const TERMINATOR_ITEMS: StyleValuesForUi<CalloutTerminator> = [
  {
    value: "arrow",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <path d="M 14 8 L 5 4.5 L 5 11.5 Z" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "dot",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="3" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "none",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <line
          x1="3"
          y1="8"
          x2="13"
          y2="8"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
];

export function SketchStylePanel() {
  const editor = useEditor();
  const tag = useValue(
    "callout tag",
    () => editor.getSharedStyles().get(CalloutTagStyle),
    [editor],
  );
  const terminator = useValue(
    "callout terminator",
    () => editor.getSharedStyles().get(CalloutTerminatorStyle),
    [editor],
  );
  const refStart = useValue(
    "dim ref start",
    () => editor.getSharedStyles().get(DimRefStartStyle),
    [editor],
  );
  const refEnd = useValue(
    "dim ref end",
    () => editor.getSharedStyles().get(DimRefEndStyle),
    [editor],
  );
  return (
    <DefaultStylePanel>
      <DefaultStylePanelContent />
      {refStart !== undefined && refEnd !== undefined && (
        <StylePanelDoubleDropdownPicker<DimRef>
          label="Dim to"
          uiTypeA="dimRefStart"
          styleA={DimRefStartStyle}
          itemsA={REF_ITEMS}
          valueA={refStart}
          labelA="Start reference"
          uiTypeB="dimRefEnd"
          styleB={DimRefEndStyle}
          itemsB={REF_ITEMS}
          valueB={refEnd}
          labelB="End reference"
        />
      )}
      {tag !== undefined && (
        <StylePanelDropdownPicker<CalloutTag>
          id="callout-tag"
          type="menu"
          label="Head"
          uiType="calloutTag"
          stylePanelType="calloutTag"
          style={CalloutTagStyle}
          items={TAG_ITEMS}
          value={tag}
        />
      )}
      {terminator !== undefined && (
        <StylePanelDropdownPicker<CalloutTerminator>
          id="callout-terminator"
          type="menu"
          label="Leader"
          uiType="calloutTerminator"
          stylePanelType="calloutTerminator"
          style={CalloutTerminatorStyle}
          items={TERMINATOR_ITEMS}
          value={terminator}
        />
      )}
    </DefaultStylePanel>
  );
}
